import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { listResourceTypes, createResourceType, deleteResourceType, type ResourceType } from '../services/authApi'

export function ResourceTypesPage() {
  const [types, setTypes] = useState<ResourceType[]>([])
  const [error, setError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      setTypes(await listResourceTypes())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function handleDelete(key: string) {
    if (!confirm(`Delete resource type "${key}"? Existing policies referencing it will remain but won't appear in the matrix.`)) return
    try {
      await deleteResourceType(key)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  const categories = new Map<string, ResourceType[]>()
  for (const t of types) {
    const cat = t.category || 'general'
    if (!categories.has(cat)) categories.set(cat, [])
    categories.get(cat)!.push(t)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h3">Resource Types</Typography>
          <Typography variant="body2" color="text.secondary">
            Platform-level registry of resource types. These appear in the policy matrix for every Research Domain.
            Adding a resource type here makes it available for role-based access control across all apps (Ros, Studio, Rival).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Resource Type
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {Array.from(categories.entries()).map(([category, catTypes]) => (
        <Box key={category} sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ mb: 1, display: 'block' }}>{category}</Typography>
          <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {catTypes.map(t => (
                  <TableRow key={t.key} hover>
                    <TableCell><Chip label={t.key} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} /></TableCell>
                    <TableCell>{t.label}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{t.description || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDelete(t.key)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      <CreateResourceTypeDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load() }}
      />
    </Box>
  )
}

function CreateResourceTypeDialog({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: () => void
}) {
  const [key, setKey] = useState('')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('general')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleCreate() {
    setError('')
    setLoading(true)
    try {
      await createResourceType({ key, label, description, category })
      setKey(''); setLabel(''); setDescription('')
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Resource Type</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField fullWidth label="Key" value={key} onChange={e => setKey(e.target.value)}
          helperText="Lowercase with colons for sub-types, e.g. participant:identity, chat:config"
          sx={{ mt: 1, mb: 2 }} />
        <TextField fullWidth label="Label" value={label} onChange={e => setLabel(e.target.value)} sx={{ mb: 2 }} />
        <TextField fullWidth label="Description" value={description} onChange={e => setDescription(e.target.value)}
          multiline rows={2} sx={{ mb: 2 }} />
        <Box>
          <Typography variant="caption" color="text.secondary">Category</Typography>
          <Select fullWidth size="small" value={category} onChange={e => setCategory(e.target.value)}>
            <MenuItem value="general">General</MenuItem>
            <MenuItem value="ros">Ros</MenuItem>
            <MenuItem value="studio">Studio</MenuItem>
            <MenuItem value="participant">Participant</MenuItem>
            <MenuItem value="engagement">Engagement</MenuItem>
            <MenuItem value="tool">Tool</MenuItem>
          </Select>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={loading || !key || !label}>
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
